import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import './Portfolio.css';

const baseURL = process.env.REACT_APP_API_BASE_URL;
const API_KEY  = process.env.REACT_APP_BROWSER_API_KEY;
const LIMIT    = 12;

const fmtINR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

// Images can be plain strings (old) or { original, thumbnail } objects (new)
const imgThumb    = (img) => (typeof img === 'string' ? img : img?.thumbnail || img?.original || '');
const imgOriginal = (img) => (typeof img === 'string' ? img : img?.original  || img?.thumbnail || '');

// ── S3 upload helper ────────────────────────────────────────────────────────
async function uploadImagesToS3(files) {
  const filesMeta = files.map((f) => ({ name: f.name, type: f.type || 'image/jpeg' }));
  const res = await axios.post(
    `${baseURL}/upload-urls`,
    { files: filesMeta, folder: 'catalogs', bucket: 'original', metadata: { asset_type: 'catalog_product' } },
    { headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY } }
  );
  const uploadUrls = res.data?.uploadUrls || [];
  const publicUrls = await Promise.all(
    uploadUrls.map(async (item, idx) => {
      await axios.put(item.url, files[idx], { headers: { 'Content-Type': files[idx].type || 'image/jpeg' } });
      return item.url.split('?')[0];
    })
  );
  return publicUrls;
}

// ── Product entry row (used inside CreateCatalogModal) ─────────────────────
function ProductEntry({ index, product, onChange, onRemove, disabled, canRemove = true }) {
  const fileRef = useRef();

  const handleField = (e) => {
    const { name, value } = e.target;
    onChange(index, { ...product, [name]: value, errors: { ...product.errors, [name]: '' } });
  };

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files).slice(0, 5 - product.images.length);
    const newImgs = picked.map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
    onChange(index, { ...product, images: [...product.images, ...newImgs].slice(0, 5) });
    e.target.value = '';
  };

  const removeImage = (i) => {
    URL.revokeObjectURL(product.images[i].preview);
    onChange(index, { ...product, images: product.images.filter((_, j) => j !== i) });
  };

  return (
    <div className="pf-pe">
      <div className="pf-pe-hdr">
        <span className="pf-pe-num">Product {index + 1}</span>
        {canRemove && (
          <button className="pf-pe-rm-btn" type="button" onClick={() => onRemove(index)} disabled={disabled}>
            Remove
          </button>
        )}
      </div>

      <div className="pf-field">
        <label>Name <span className="pf-req">*</span></label>
        <input name="name" value={product.name} onChange={handleField} placeholder="e.g. John weds Jane – Destination Album" className={`pf-input${product.errors?.name ? ' pf-input--err' : ''}`} disabled={disabled} />
        {product.errors?.name && <span className="pf-err-txt">{product.errors.name}</span>}
      </div>

      <div className="pf-field">
        <label>Description <span className="pf-optional">(optional)</span></label>
        <textarea name="description" value={product.description} onChange={handleField} placeholder="Brief description of this product…" rows={2} className="pf-input pf-textarea" disabled={disabled} />
      </div>

      <div className="pf-row-2">
        <div className="pf-field">
          <label>MRP (₹) <span className="pf-req">*</span></label>
          <input name="mrp" type="number" min="0" value={product.mrp} onChange={handleField} placeholder="150000" className={`pf-input${product.errors?.mrp ? ' pf-input--err' : ''}`} disabled={disabled} />
          {product.errors?.mrp && <span className="pf-err-txt">{product.errors.mrp}</span>}
        </div>
        <div className="pf-field">
          <label>Selling Price (₹) <span className="pf-req">*</span></label>
          <input name="selling_price" type="number" min="0" value={product.selling_price} onChange={handleField} placeholder="125000" className={`pf-input${product.errors?.selling_price ? ' pf-input--err' : ''}`} disabled={disabled} />
          {product.errors?.selling_price && <span className="pf-err-txt">{product.errors.selling_price}</span>}
        </div>
      </div>

      <div className="pf-field">
        <label>Images <span className="pf-req">*</span> <span className="pf-optional">(up to 5)</span></label>
        <div
          className={`pf-upload-zone${product.images.length >= 5 ? ' pf-upload-zone--full' : ''}${product.errors?.images ? ' pf-upload-zone--err' : ''}`}
          onClick={() => product.images.length < 5 && !disabled && fileRef.current.click()}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          <span>{product.images.length >= 5 ? 'Max 5 images added' : 'Click to upload images'}</span>
          <span className="pf-upload-hint">PNG, JPG</span>
        </div>
        {product.errors?.images && <span className="pf-err-txt">{product.errors.images}</span>}
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} disabled={disabled} />
        {product.images.length > 0 && (
          <div className="pf-thumb-row">
            {product.images.map((img, i) => (
              <div key={i} className="pf-thumb">
                <img src={img.preview} alt="" />
                <button className="pf-thumb-rm" type="button" onClick={() => removeImage(i)} disabled={disabled}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const emptyProduct = () => ({ name: '', description: '', mrp: '', selling_price: '', images: [], errors: {} });

// ── Create Catalogue Modal ──────────────────────────────────────────────────
function CreateCatalogModal({ onClose, onSuccess }) {
  const [form, setForm]         = useState({ catalog_name: '', description: '' });
  const [products, setProducts] = useState([emptyProduct()]);
  const [saving, setSaving]     = useState(false);
  const [errors, setErrors]     = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const updateProduct = (idx, updated) =>
    setProducts((p) => p.map((prod, i) => (i === idx ? updated : prod)));

  const removeProduct = (idx) => {
    if (products.length === 1) return;
    products[idx].images.forEach((img) => URL.revokeObjectURL(img.preview));
    setProducts((p) => p.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const e = {};
    if (!form.catalog_name.trim()) e.catalog_name = 'Catalogue name is required';

    let productsValid = true;
    const validated = products.map((prod) => {
      const pe = {};
      if (!prod.name.trim()) { pe.name = 'Product name is required'; productsValid = false; }
      if (prod.images.length < 3) { pe.images = 'At least 3 images are required'; productsValid = false; }
      if (!prod.mrp || isNaN(Number(prod.mrp)) || Number(prod.mrp) <= 0) { pe.mrp = 'Enter a valid MRP'; productsValid = false; }
      if (!prod.selling_price || isNaN(Number(prod.selling_price)) || Number(prod.selling_price) <= 0) { pe.selling_price = 'Enter a valid selling price'; productsValid = false; }
      return { ...prod, errors: pe };
    });
    setProducts(validated);
    setErrors(e);
    return !Object.keys(e).length && productsValid;
  };

  const handleSubmit = async () => {
    if (!validate() || saving) return;
    const vendorId = localStorage.getItem('vendorId');
    try {
      setSaving(true);
      const productsPayload = await Promise.all(
        products.map(async (prod) => ({
          name: prod.name.trim(),
          description: prod.description.trim(),
          images: prod.images.length ? await uploadImagesToS3(prod.images.map((i) => i.file)) : [],
          mrp: Number(prod.mrp),
          selling_price: Number(prod.selling_price),
        }))
      );
      const payload = { vendor_id: vendorId, catalog_name: form.catalog_name.trim(), description: form.description.trim(), products: productsPayload };
      const res = await axios.post(
        `${baseURL}/photographer/catalogs`,
        payload,
        { headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY } }
      );
      onSuccess(res.data);
    } catch (err) {
      setErrors({ api: err?.response?.data?.message || err?.response?.data?.errorMessage || 'Failed to create catalogue.' });
    } finally {
      setSaving(false);
    }
  };

  const hasImages = products.some((p) => p.images.length > 0);

  return (
    <div className="pf-overlay" onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="pf-modal">
        <div className="pf-modal-hdr">
          <h2>New Catalogue</h2>
          <button className="pf-modal-x" onClick={onClose} disabled={saving}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="pf-modal-body">
          {errors.api && <p className="pf-api-err">⚠️ {errors.api}</p>}

          {/* ── Catalogue details ── */}
          <div className="pf-section-label">Catalogue Details</div>

          <div className="pf-field">
            <label>Catalogue Name <span className="pf-req">*</span></label>
            <input name="catalog_name" value={form.catalog_name} onChange={handleChange} placeholder="e.g. Premium Wedding Packages 2026" className={`pf-input${errors.catalog_name ? ' pf-input--err' : ''}`} disabled={saving} />
            {errors.catalog_name && <span className="pf-err-txt">{errors.catalog_name}</span>}
          </div>

          <div className="pf-field">
            <label>Description <span className="pf-optional">(optional)</span></label>
            <textarea name="description" value={form.description} onChange={handleChange} placeholder="Brief description of this catalogue…" rows={2} className="pf-input pf-textarea" disabled={saving} />
          </div>

          {/* ── Products ── */}
          <div className="pf-products-hdr">
            <span className="pf-section-label" style={{ margin: 0 }}>
              Products <span className="pf-req">*</span>
              {products.length > 0 && <span className="pf-product-count-badge">{products.length}</span>}
            </span>
            <button className="pf-add-prod-btn" type="button" onClick={() => setProducts((p) => [...p, emptyProduct()])} disabled={saving}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Product
            </button>
          </div>

          <>
            {products.map((prod, idx) => (
              <ProductEntry
                key={idx}
                index={idx}
                product={prod}
                onChange={updateProduct}
                onRemove={removeProduct}
                disabled={saving}
                canRemove={products.length > 1}
              />
            ))}
            <button className="pf-add-another-btn" type="button" onClick={() => setProducts((p) => [...p, emptyProduct()])} disabled={saving}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Another Product
            </button>
          </>
        </div>

        <div className="pf-modal-ftr">
          <button className="pf-btn-sec" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="pf-btn-pri" onClick={handleSubmit} disabled={saving}>
            {saving
              ? <><span className="pf-spin" />{hasImages ? ' Uploading…' : ' Creating…'}</>
              : `Create with ${products.length} Product${products.length > 1 ? 's' : ''}`
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Product Modal ───────────────────────────────────────────────────────
function AddProductModal({ catalogId, onClose, onSuccess }) {
  const [form, setForm]         = useState({ name: '', description: '', mrp: '', selling_price: '' });
  const [images, setImages]     = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors]     = useState({});
  const fileRef                 = useRef();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files).slice(0, 5 - images.length);
    const newImgs = picked.map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
    setImages((p) => [...p, ...newImgs].slice(0, 5));
    e.target.value = '';
  };

  const removeImage = (idx) => {
    setImages((p) => {
      URL.revokeObjectURL(p[idx].preview);
      return p.filter((_, i) => i !== idx);
    });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Product name is required';
    if (images.length < 3) e.images = 'At least 3 images are required';
    if (!form.mrp || isNaN(Number(form.mrp)) || Number(form.mrp) <= 0) e.mrp = 'Enter a valid MRP';
    if (!form.selling_price || isNaN(Number(form.selling_price)) || Number(form.selling_price) <= 0)
      e.selling_price = 'Enter a valid selling price';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate() || uploading) return;
    const vendorId = localStorage.getItem('vendorId');
    try {
      setUploading(true);
      const imageUrls = images.length ? await uploadImagesToS3(images.map((i) => i.file)) : [];
      const addPayload = {
        vendor_id: vendorId,
        catalog_id: catalogId,
        products: [{
          name: form.name.trim(),
          description: form.description.trim(),
          images: imageUrls,
          mrp: Number(form.mrp),
          selling_price: Number(form.selling_price),
        }],
      };
      const addRes = await axios.post(
        `${baseURL}/photographer/catalogs/products`,
        addPayload,
        { headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY } }
      );
      onSuccess();
      onClose();
    } catch (err) {
      setErrors({ api: err?.response?.data?.message || err?.response?.data?.errorMessage || 'Failed to add product.' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="pf-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pf-modal">
        <div className="pf-modal-hdr">
          <h2>Add Product</h2>
          <button className="pf-modal-x" onClick={onClose} disabled={uploading}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="pf-modal-body">
          {errors.api && <p className="pf-api-err">⚠️ {errors.api}</p>}

          <div className="pf-field">
            <label>Product Name <span className="pf-req">*</span></label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. John weds Jane – Destination Album"
              className={`pf-input${errors.name ? ' pf-input--err' : ''}`}
            />
            {errors.name && <span className="pf-err-txt">{errors.name}</span>}
          </div>

          <div className="pf-field">
            <label>Description <span className="pf-optional">(optional)</span></label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Brief description of this product…"
              rows={3}
              className="pf-input pf-textarea"
            />
          </div>

          <div className="pf-row-2">
            <div className="pf-field">
              <label>MRP (₹) <span className="pf-req">*</span></label>
              <input
                name="mrp"
                type="number"
                min="0"
                value={form.mrp}
                onChange={handleChange}
                placeholder="150000"
                className={`pf-input${errors.mrp ? ' pf-input--err' : ''}`}
              />
              {errors.mrp && <span className="pf-err-txt">{errors.mrp}</span>}
            </div>
            <div className="pf-field">
              <label>Selling Price (₹) <span className="pf-req">*</span></label>
              <input
                name="selling_price"
                type="number"
                min="0"
                value={form.selling_price}
                onChange={handleChange}
                placeholder="125000"
                className={`pf-input${errors.selling_price ? ' pf-input--err' : ''}`}
              />
              {errors.selling_price && <span className="pf-err-txt">{errors.selling_price}</span>}
            </div>
          </div>

          <div className="pf-field">
            <label>
              Images <span className="pf-req">*</span>
              <span className="pf-optional"> (up to 5)</span>
            </label>
            <div
              className={`pf-upload-zone${images.length >= 5 ? ' pf-upload-zone--full' : ''}${errors.images ? ' pf-upload-zone--err' : ''}`}
              onClick={() => images.length < 5 && fileRef.current.click()}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <span>{images.length >= 5 ? 'Maximum 5 images added' : 'Click to upload images'}</span>
              <span className="pf-upload-hint">PNG, JPG</span>
            </div>
            {errors.images && <span className="pf-err-txt">{errors.images}</span>}
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} />

            {images.length > 0 && (
              <div className="pf-thumb-row">
                {images.map((img, i) => (
                  <div key={i} className="pf-thumb">
                    <img src={img.preview} alt="" />
                    <button className="pf-thumb-rm" onClick={() => removeImage(i)} disabled={uploading}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="pf-modal-ftr">
          <button className="pf-btn-sec" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="pf-btn-pri" onClick={handleSubmit} disabled={uploading}>
            {uploading ? <><span className="pf-spin" /> {images.length ? 'Uploading…' : 'Saving…'}</> : 'Add Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Catalogue Modal ────────────────────────────────────────────────────
function EditCatalogModal({ catalog, onClose, onSuccess }) {
  const [form, setForm]     = useState({ catalog_name: catalog.catalog_name, description: catalog.description || '' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.catalog_name.trim()) e.catalog_name = 'Catalogue name is required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate() || saving) return;
    const vendorId = localStorage.getItem('vendorId');
    try {
      setSaving(true);
      const editPayload = { vendor_id: vendorId, catalog_id: catalog.catalog_id, catalog_name: form.catalog_name.trim(), description: form.description.trim() };
      const res = await axios.patch(
        `${baseURL}/photographer/catalogs`,
        editPayload,
        { headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY } }
      );
      onSuccess({ ...catalog, catalog_name: form.catalog_name.trim(), description: form.description.trim(), ...(res.data?.catalog || {}) });
      onClose();
    } catch (err) {
      setErrors({ api: err?.response?.data?.message || err?.response?.data?.errorMessage || 'Failed to update catalogue.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pf-overlay" onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="pf-modal pf-modal--sm">
        <div className="pf-modal-hdr">
          <h2>Edit Catalogue</h2>
          <button className="pf-modal-x" onClick={onClose} disabled={saving}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="pf-modal-body">
          {errors.api && <p className="pf-api-err">⚠️ {errors.api}</p>}

          <div className="pf-field">
            <label>Catalogue Name <span className="pf-req">*</span></label>
            <input name="catalog_name" value={form.catalog_name} onChange={handleChange} placeholder="e.g. Premium Wedding Packages 2026" className={`pf-input${errors.catalog_name ? ' pf-input--err' : ''}`} disabled={saving} />
            {errors.catalog_name && <span className="pf-err-txt">{errors.catalog_name}</span>}
          </div>

          <div className="pf-field">
            <label>Description <span className="pf-optional">(optional)</span></label>
            <textarea name="description" value={form.description} onChange={handleChange} placeholder="Brief description of this catalogue…" rows={3} className="pf-input pf-textarea" disabled={saving} />
          </div>
        </div>

        <div className="pf-modal-ftr">
          <button className="pf-btn-sec" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="pf-btn-pri" onClick={handleSubmit} disabled={saving}>
            {saving ? <><span className="pf-spin" /> Saving…</> : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Product Modal ──────────────────────────────────────────────────────
function EditProductModal({ product, onClose, onSuccess }) {
  const [form, setForm]               = useState({ name: product.name || '', description: product.description || '', mrp: String(product.mrp || ''), selling_price: String(product.selling_price || '') });
  const [existingImgs, setExistingImgs] = useState(product.images || []);
  const [newImgs, setNewImgs]         = useState([]);
  const [uploading, setUploading]     = useState(false);
  const [errors, setErrors]           = useState({});
  const fileRef                       = useRef();

  const totalImgs = existingImgs.length + newImgs.length;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files).slice(0, 5 - totalImgs);
    const added = picked.map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
    setNewImgs((p) => [...p, ...added].slice(0, 5 - existingImgs.length));
    if (errors.images) setErrors((p) => ({ ...p, images: '' }));
    e.target.value = '';
  };

  const removeExisting = (idx) => {
    setExistingImgs((p) => p.filter((_, i) => i !== idx));
    if (errors.images) setErrors((p) => ({ ...p, images: '' }));
  };

  const removeNew = (idx) => {
    setNewImgs((p) => { URL.revokeObjectURL(p[idx].preview); return p.filter((_, i) => i !== idx); });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Product name is required';
    if (existingImgs.length + newImgs.length === 0) e.images = 'At least one image is required';
    if (!form.mrp || isNaN(Number(form.mrp)) || Number(form.mrp) <= 0) e.mrp = 'Enter a valid MRP';
    if (!form.selling_price || isNaN(Number(form.selling_price)) || Number(form.selling_price) <= 0) e.selling_price = 'Enter a valid selling price';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate() || uploading) return;
    try {
      setUploading(true);
      const uploadedUrls = newImgs.length ? await uploadImagesToS3(newImgs.map((i) => i.file)) : [];
      const allImages = [...existingImgs, ...uploadedUrls];
      const editProductPayload = { product_id: product.product_id || product.id, name: form.name.trim(), description: form.description.trim(), images: allImages, mrp: Number(form.mrp), selling_price: Number(form.selling_price) };
      const editProdRes = await axios.patch(
        `${baseURL}/photographer/catalogs/products`,
        editProductPayload,
        { headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY } }
      );
      onSuccess();
      onClose();
    } catch (err) {
      setErrors({ api: err?.response?.data?.message || err?.response?.data?.errorMessage || 'Failed to update product.' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="pf-overlay" onClick={(e) => e.target === e.currentTarget && !uploading && onClose()}>
      <div className="pf-modal">
        <div className="pf-modal-hdr">
          <h2>Edit Product</h2>
          <button className="pf-modal-x" onClick={onClose} disabled={uploading}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="pf-modal-body">
          {errors.api && <p className="pf-api-err">⚠️ {errors.api}</p>}

          <div className="pf-field">
            <label>Product Name <span className="pf-req">*</span></label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. John weds Jane – Destination Album" className={`pf-input${errors.name ? ' pf-input--err' : ''}`} disabled={uploading} />
            {errors.name && <span className="pf-err-txt">{errors.name}</span>}
          </div>

          <div className="pf-field">
            <label>Description <span className="pf-optional">(optional)</span></label>
            <textarea name="description" value={form.description} onChange={handleChange} placeholder="Brief description…" rows={3} className="pf-input pf-textarea" disabled={uploading} />
          </div>

          <div className="pf-row-2">
            <div className="pf-field">
              <label>MRP (₹) <span className="pf-req">*</span></label>
              <input name="mrp" type="number" min="0" value={form.mrp} onChange={handleChange} placeholder="150000" className={`pf-input${errors.mrp ? ' pf-input--err' : ''}`} disabled={uploading} />
              {errors.mrp && <span className="pf-err-txt">{errors.mrp}</span>}
            </div>
            <div className="pf-field">
              <label>Selling Price (₹) <span className="pf-req">*</span></label>
              <input name="selling_price" type="number" min="0" value={form.selling_price} onChange={handleChange} placeholder="125000" className={`pf-input${errors.selling_price ? ' pf-input--err' : ''}`} disabled={uploading} />
              {errors.selling_price && <span className="pf-err-txt">{errors.selling_price}</span>}
            </div>
          </div>

          <div className="pf-field">
            <label>Images <span className="pf-req">*</span> <span className="pf-optional">(up to 5)</span></label>

            {/* Existing + new image thumbnails */}
            {(existingImgs.length > 0 || newImgs.length > 0) && (
              <div className="pf-thumb-row">
                {existingImgs.map((img, i) => (
                  <div key={`ex-${i}`} className="pf-thumb">
                    <img src={imgThumb(img)} alt="" onError={(e) => { e.target.onerror = null; e.target.src = ''; }} />
                    <button className="pf-thumb-rm" type="button" onClick={() => removeExisting(i)} disabled={uploading}>×</button>
                  </div>
                ))}
                {newImgs.map((img, i) => (
                  <div key={`nw-${i}`} className="pf-thumb pf-thumb--new">
                    <img src={img.preview} alt="" />
                    <button className="pf-thumb-rm" type="button" onClick={() => removeNew(i)} disabled={uploading}>×</button>
                  </div>
                ))}
              </div>
            )}

            {totalImgs < 5 && (
              <div className={`pf-upload-zone${errors.images ? ' pf-upload-zone--err' : ''}`} onClick={() => !uploading && fileRef.current.click()}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>Click to add more images</span>
                <span className="pf-upload-hint">PNG, JPG · {5 - totalImgs} slot{5 - totalImgs !== 1 ? 's' : ''} remaining</span>
              </div>
            )}
            {errors.images && <span className="pf-err-txt">{errors.images}</span>}
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} disabled={uploading} />
          </div>
        </div>

        <div className="pf-modal-ftr">
          <button className="pf-btn-sec" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="pf-btn-pri" onClick={handleSubmit} disabled={uploading}>
            {uploading ? <><span className="pf-spin" />{newImgs.length ? ' Uploading…' : ' Saving…'}</> : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Product View Modal ──────────────────────────────────────────────────────
function ProductViewModal({ product, onClose }) {
  const [imgIdx, setImgIdx] = useState(0);
  const images = product.images || [];

  const prev = () => setImgIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setImgIdx((i) => (i + 1) % images.length);

  const discount = product.mrp > product.selling_price
    ? Math.round(((product.mrp - product.selling_price) / product.mrp) * 100)
    : 0;

  return (
    <div className="pf-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pf-modal pf-pv-modal">
        <button className="pf-modal-x pf-pv-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Image carousel */}
        <div className="pf-pv-img-wrap">
          {images.length > 0 ? (
            <>
              <img src={imgOriginal(images[imgIdx])} alt={product.name} className="pf-pv-img" />
              {images.length > 1 && (
                <>
                  <button className="pf-pv-arrow pf-pv-arrow--left" onClick={prev}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <button className="pf-pv-arrow pf-pv-arrow--right" onClick={next}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                  <div className="pf-pv-dots">
                    {images.map((_, i) => (
                      <button key={i} className={`pf-pv-dot${i === imgIdx ? ' pf-pv-dot--active' : ''}`} onClick={() => setImgIdx(i)} />
                    ))}
                  </div>
                  <span className="pf-pv-img-count">{imgIdx + 1} / {images.length}</span>
                </>
              )}
            </>
          ) : (
            <div className="pf-pv-img-ph">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <span>No images</span>
            </div>
          )}
        </div>

        {/* Product info */}
        <div className="pf-pv-body">
          <h2 className="pf-pv-name">{product.name}</h2>
          {product.description && <p className="pf-pv-desc">{product.description}</p>}
          <div className="pf-pv-price-row">
            {discount > 0 && <span className="pf-mrp">{fmtINR(product.mrp)}</span>}
            <span className="pf-pv-price">{fmtINR(product.selling_price)}</span>
            {discount > 0 && <span className="pf-discount-badge">{discount}% off</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Catalogue Detail View ───────────────────────────────────────────────────
function CatalogDetail({ catalog, onBack, onEdit, onEditSuccess }) {
  const [products, setProducts]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [page, setPage]                   = useState(1);
  const [totalPages, setTotalPages]       = useState(1);
  const [showAddProduct, setShowAddProduct]           = useState(false);
  const [viewProduct, setViewProduct]                 = useState(null);
  const [editProduct, setEditProduct]                 = useState(null);
  const [confirmDeleteProductId, setConfirmDeleteProductId] = useState(null);
  const [deletingProduct, setDeletingProduct]         = useState(false);

  const fetchProducts = async (p = 1) => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${baseURL}/photographer/catalogs/products`, {
        params: { catalog_id: catalog.catalog_id, page: p, limit: LIMIT },
        headers: { 'x-api-key': API_KEY },
      });
      const data = res.data;
      setProducts(data.products || []);
      const total = data.total_count ?? data.total ?? (data.products?.length ?? 0);
      setTotalPages(Math.max(1, Math.ceil(total / LIMIT)));
      setPage(p);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load products.');
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    try {
      setDeletingProduct(true);
      await axios.delete(`${baseURL}/photographer/catalogs/products`, {
        params: { product_id: productId },
        headers: { 'x-api-key': API_KEY },
      });
      setConfirmDeleteProductId(null);
      setProducts((prev) => prev.filter((p) => (p.product_id || p.id) !== productId));
    } catch (err) {
      setConfirmDeleteProductId(null);
      setError(err?.response?.data?.message || err?.response?.data?.errorMessage || 'Failed to delete product.');
    } finally {
      setDeletingProduct(false);
    }
  };

  useEffect(() => { fetchProducts(1); }, [catalog.catalog_id]);

  return (
    <>
      {/* Detail top bar */}
      <div className="pf-detail-bar">
        <button className="pf-back-btn" onClick={onBack}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          All Catalogues
        </button>
        <div className="pf-detail-bar-right">
          <button className="pf-btn-sec pf-btn-edit" onClick={() => onEdit && onEdit(catalog)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit
          </button>
          <button className="pf-btn-pri" onClick={() => setShowAddProduct(true)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Product
          </button>
        </div>
      </div>

      {/* Catalogue info */}
      <div className="pf-catalog-info">
        <h2 className="pf-catalog-detail-title">{catalog.catalog_name}</h2>
        {catalog.description && <p className="pf-catalog-detail-desc">{catalog.description}</p>}
      </div>

      {/* Products */}
      {loading ? (
        <div className="pf-loader"><div className="pf-spin-lg"/><span>Loading products…</span></div>
      ) : error ? (
        <div className="pf-error-box">⚠️ {error}</div>
      ) : products.length === 0 ? (
        <div className="pf-empty">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#e5e7eb" strokeWidth="1">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          <p>No products yet. Add your first product to this catalogue.</p>
          <button className="pf-btn-pri" onClick={() => setShowAddProduct(true)}>Add Product</button>
        </div>
      ) : (
        <div className="pf-product-grid">
          {products.map((prod) => (
            <div key={prod.product_id || prod.id} className="pf-product-card" onClick={() => setViewProduct(prod)}>
              <div className="pf-product-img-wrap">
                {prod.images?.[0] ? (
                  <img src={imgThumb(prod.images[0])} alt={prod.name} className="pf-product-img" onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.style.display = 'none'; }} />
                ) : (
                  <div className="pf-product-img-ph">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.2">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  </div>
                )}
                {prod.images?.length > 1 && (
                  <span className="pf-img-more">+{prod.images.length - 1}</span>
                )}
                <div className="pf-product-view-overlay">
                  <span className="pf-product-view-btn">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    View
                  </span>
                </div>
              </div>
              <div className="pf-product-body">
                <div className="pf-product-body-top">
                  <h3 className="pf-product-name">{prod.name}</h3>
                  <div className="pf-catalog-actions" onClick={(e) => e.stopPropagation()}>
                    {confirmDeleteProductId === (prod.product_id || prod.id) ? (
                      <div className="pf-delete-confirm">
                        <span className="pf-delete-confirm-txt">Delete?</span>
                        <button className="pf-confirm-cancel-btn" onClick={() => setConfirmDeleteProductId(null)} disabled={deletingProduct}>Cancel</button>
                        <button className="pf-confirm-delete-btn" onClick={() => deleteProduct(prod.product_id || prod.id)} disabled={deletingProduct}>
                          {deletingProduct ? <span className="pf-spin" /> : 'Delete'}
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          className="pf-card-edit-btn"
                          title="Edit product"
                          onClick={() => setEditProduct(prod)}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button
                          className="pf-card-edit-btn pf-card-delete-btn"
                          title="Delete product"
                          onClick={() => setConfirmDeleteProductId(prod.product_id || prod.id)}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {prod.description && <p className="pf-product-desc">{prod.description}</p>}
                <div className="pf-price-row">
                  {prod.mrp !== prod.selling_price && (
                    <span className="pf-mrp">{fmtINR(prod.mrp)}</span>
                  )}
                  <span className="pf-price">{fmtINR(prod.selling_price)}</span>
                  {prod.mrp > prod.selling_price && (
                    <span className="pf-discount-badge">
                      {Math.round(((prod.mrp - prod.selling_price) / prod.mrp) * 100)}% off
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="pf-pagination">
          <button className="pf-pg-btn pf-pg-nav" onClick={() => fetchProducts(page - 1)} disabled={page === 1}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button key={p} className={`pf-pg-btn${p === page ? ' pf-pg-active' : ''}`} onClick={() => fetchProducts(p)}>{p}</button>
          ))}
          <button className="pf-pg-btn pf-pg-nav" onClick={() => fetchProducts(page + 1)} disabled={page === totalPages}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      )}

      {viewProduct && (
        <ProductViewModal product={viewProduct} onClose={() => setViewProduct(null)} />
      )}

      {editProduct && (
        <EditProductModal
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSuccess={() => { setEditProduct(null); fetchProducts(page); }}
        />
      )}

      {showAddProduct && (
        <AddProductModal
          catalogId={catalog.catalog_id}
          onClose={() => setShowAddProduct(false)}
          onSuccess={() => fetchProducts(page)}
        />
      )}
    </>
  );
}

// ── Main Portfolio Page ─────────────────────────────────────────────────────
export default function Portfolio() {
  const history = useHistory();
  const [catalogs, setCatalogs]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [page, setPage]                   = useState(1);
  const [totalPages, setTotalPages]       = useState(1);
  const [showCreate, setShowCreate]         = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [editCatalog, setEditCatalog]       = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting]             = useState(false);

  const fetchCatalogs = async (p = 1) => {
    const vendorId = localStorage.getItem('vendorId');
    if (!vendorId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${baseURL}/photographer/catalogs`, {
        params: { vendor_id: vendorId, page: p, limit: LIMIT },
        headers: { 'x-api-key': API_KEY },
      });
      const data = res.data;
      const cats = data.catalogs || [];
      const total = data.total_count ?? data.total ?? cats.length;
      setTotalPages(Math.max(1, Math.ceil(total / LIMIT)));
      setPage(p);

      // Fetch first product image for each catalogue that has no thumbnail
      const withImages = await Promise.all(
        cats.map(async (c) => {
          const existing = c.thumbnail || c.cover_image || c.cover_photo
            || imgThumb(c.products?.[0]?.images?.[0]) || c.first_product_image;
          if (existing) return c;
          try {
            const pr = await axios.get(`${baseURL}/photographer/catalogs/products`, {
              params: { catalog_id: c.catalog_id, page: 1, limit: 1 },
              headers: { 'x-api-key': API_KEY },
            });
            const firstImg = imgThumb(pr.data?.products?.[0]?.images?.[0]) || null;
            return firstImg ? { ...c, thumbnail: firstImg } : c;
          } catch {
            return c;
          }
        })
      );
      setCatalogs(withImages);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load catalogues.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCatalogs(1); }, []);

  const deleteCatalog = async (catalogId) => {
    const vendorId = localStorage.getItem('vendorId');
    try {
      setDeleting(true);
      await axios.delete(`${baseURL}/photographer/catalogs`, {
        params: { catalog_id: catalogId, vendor_id: vendorId },
        headers: { 'x-api-key': API_KEY },
      });
      setConfirmDeleteId(null);
      setCatalogs((prev) => prev.filter((c) => c.catalog_id !== catalogId));
    } catch (err) {
      setConfirmDeleteId(null);
      setError(err?.response?.data?.message || 'Failed to delete catalogue.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateSuccess = (responseData) => {
    setShowCreate(false);
    const newCatalog = responseData?.catalog || responseData?.data || responseData;
    fetchCatalogs(1);
    if (newCatalog?.catalog_id) setSelectedCatalog(newCatalog);
  };

  return (
    <div className="pf-wrapper">
      <div className="pf-container">

        {selectedCatalog ? (
          <CatalogDetail
            catalog={selectedCatalog}
            onBack={() => { setSelectedCatalog(null); fetchCatalogs(page); }}
            onEdit={(c) => setEditCatalog(c)}
            onEditSuccess={(updated) => setSelectedCatalog(updated)}
          />
        ) : (
          <>
            {/* Page header */}
            <div className="pf-page-hdr">
              <div>
                <button className="pf-back-btn" onClick={() => history.goBack()}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                  Back
                </button>
                <h1 className="pf-title">Portfolio</h1>
                <p className="pf-subtitle">Manage your catalogues and showcase your work.</p>
              </div>
              <button className="pf-btn-pri" onClick={() => setShowCreate(true)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                New Catalogue
              </button>
            </div>

            {/* Catalogue list */}
            {loading ? (
              <div className="pf-loader"><div className="pf-spin-lg"/><span>Loading catalogues…</span></div>
            ) : error ? (
              <div className="pf-error-box">⚠️ {error}</div>
            ) : catalogs.length === 0 ? (
              <div className="pf-empty">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#e5e7eb" strokeWidth="1">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>
                <p>No catalogues yet. Create your first one to get started.</p>
                <button className="pf-btn-pri" onClick={() => setShowCreate(true)}>Create Catalogue</button>
              </div>
            ) : (
              <div className="pf-catalog-grid">
                {catalogs.map((c) => (
                  <div key={c.catalog_id} className="pf-catalog-card" onClick={() => setSelectedCatalog(c)}>
                    <div className="pf-catalog-thumb">
                      {c.thumbnail || c.cover_image || c.cover_photo ? (
                        <img src={c.thumbnail || c.cover_image || c.cover_photo} alt={c.catalog_name} onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="pf-catalog-thumb-ph">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="pf-catalog-body">
                      <h3 className="pf-catalog-name">{c.catalog_name}</h3>
                      {c.description && <p className="pf-catalog-desc">{c.description}</p>}
                      <div className="pf-catalog-footer">
                        {c.product_count != null && (
                          <span className="pf-catalog-count">
                            {c.product_count} product{c.product_count !== 1 ? 's' : ''}
                          </span>
                        )}
                        <div className="pf-catalog-actions">
                          {confirmDeleteId === c.catalog_id ? (
                            <div className="pf-delete-confirm" onClick={(e) => e.stopPropagation()}>
                              <span className="pf-delete-confirm-txt">Delete?</span>
                              <button className="pf-confirm-cancel-btn" onClick={() => setConfirmDeleteId(null)} disabled={deleting}>Cancel</button>
                              <button className="pf-confirm-delete-btn" onClick={() => deleteCatalog(c.catalog_id)} disabled={deleting}>
                                {deleting ? <span className="pf-spin" /> : 'Delete'}
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                className="pf-card-edit-btn"
                                title="Edit catalogue"
                                onClick={(e) => { e.stopPropagation(); setEditCatalog(c); }}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                              </button>
                              <button
                                className="pf-card-edit-btn pf-card-delete-btn"
                                title="Delete catalogue"
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(c.catalog_id); }}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                </svg>
                              </button>
                              <span className="pf-catalog-cta">View →</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="pf-pagination">
                <button className="pf-pg-btn pf-pg-nav" onClick={() => fetchCatalogs(page - 1)} disabled={page === 1}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…'); acc.push(p); return acc; }, [])
                  .map((p, i) => p === '…'
                    ? <span key={`e${i}`} className="pf-pg-ellipsis">…</span>
                    : <button key={p} className={`pf-pg-btn${p === page ? ' pf-pg-active' : ''}`} onClick={() => fetchCatalogs(p)}>{p}</button>
                  )}
                <button className="pf-pg-btn pf-pg-nav" onClick={() => fetchCatalogs(page + 1)} disabled={page === totalPages}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showCreate && (
        <CreateCatalogModal onClose={() => setShowCreate(false)} onSuccess={handleCreateSuccess} />
      )}

      {editCatalog && (
        <EditCatalogModal
          catalog={editCatalog}
          onClose={() => setEditCatalog(null)}
          onSuccess={(updated) => {
            setEditCatalog(null);
            setCatalogs((prev) => prev.map((c) => c.catalog_id === updated.catalog_id ? { ...c, ...updated } : c));
            if (selectedCatalog?.catalog_id === updated.catalog_id) setSelectedCatalog((p) => ({ ...p, ...updated }));
          }}
        />
      )}
    </div>
  );
}
